import React, { useState, useEffect } from 'react';
import { Copy, ClipboardCheck } from 'lucide-react'; // Changed CopyCheck to ClipboardCheck
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { supabase } from '@/integrations/supabase/client';
// Removed QRCodeSVG import

interface PixPaymentProps {
  totalAmount: number;
  onReceiptUpload: (base64Image: string) => void;
  onPixKeyChange: (pixKey: string) => void;
}

interface PaymentInfo {
  pix_key?: string;
}

const PixPayment: React.FC<PixPaymentProps> = ({
  totalAmount,
  onReceiptUpload,
  onPixKeyChange,
}) => {
  const [copied, setCopied] = useState(false);
  const [pixKey, setPixKey] = useState('');
  const [storeName, setStoreName] = useState('');
  const [clientPixKey, setClientPixKey] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch PIX key from system settings
  useEffect(() => {
    const fetchPixKey = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('store_name, payment_info')
          .order('updated_at', { ascending: false })
          .limit(1)
          .single();

        if (error) {
          console.error('Error fetching PIX key:', error);
          return;
        }

        if (data) {
          setStoreName(data.store_name || '');
          // Check if payment_info exists and has expected structure
          const paymentInfo = typeof data.payment_info === 'object' ? data.payment_info as PaymentInfo : {};
          if (paymentInfo && paymentInfo.pix_key) {
            setPixKey(paymentInfo.pix_key);
          }
        }
      } catch (error) {
        console.error('Error fetching PIX key:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPixKey();
  }, []);

  const handleCopyPixKey = () => {
    navigator.clipboard.writeText(pixKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePixKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientPixKey(e.target.value);
    onPixKeyChange(e.target.value);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        onReceiptUpload(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  if (isLoading) {
    return <div>Carregando informações de pagamento...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800">
        <h3 className="font-medium text-amber-900 mb-2">Instruções para pagamento via PIX</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li>Copie a chave PIX abaixo ou escaneie o QR Code</li> {/* Updated instruction */}
          <li>Abra o aplicativo do seu banco</li>
          <li>Realize uma transferência PIX</li>
          <li>Envie o comprovante de pagamento</li>
        </ol>
      </div>

      <div className="border rounded-md p-4 space-y-4">
        <div>
          <Label>Loja</Label>
          <div className="font-medium">{storeName}</div>
        </div>

        <div>
          <Label>Valor</Label>
          <div className="font-bold text-lg">
            {totalAmount.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
          </div>
        </div>

        <Separator />

        {/* Only show PIX details if a key is configured */}
        {pixKey && (
          <>
            <div>
              <Label htmlFor="pix-key" className="mb-1 block">
                Chave PIX
              </Label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-2 bg-gray-50 border rounded truncate text-sm">
                  {pixKey || 'Chave PIX não configurada'}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyPixKey}
                  disabled={!pixKey}
                >
                  {/* Changed CopyCheck to ClipboardCheck */}
                  {copied ? <ClipboardCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="flex justify-center py-2">
              <div className="bg-gray-100 p-3 rounded-lg border border-gray-200">
                <p className="text-center text-sm text-gray-700 mb-2">QR Code PIX</p>
                <div className="bg-white p-2 border border-gray-200 rounded">
                  {/* Replaced dynamic QR code with static image */}
                  <img 
                    src="/pix_qrcode.png" 
                    alt="QR Code PIX" 
                    width={150} 
                    height={150} 
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Keep the receipt upload and client key fields */}
      <div className="border rounded-md p-4 space-y-4">
        <div>
          <Label htmlFor="client-pix" className="mb-1 block">
            Sua chave PIX (para identificação)
          </Label>
          <Input
            id="client-pix"
            value={clientPixKey}
            onChange={handlePixKeyChange}
            placeholder="Informe a chave PIX utilizada no pagamento"
          />
          <p className="text-xs text-gray-500 mt-1">
            Informe a chave PIX que você utilizou para fazer o pagamento
          </p>
        </div>

        <div>
          <Label htmlFor="receipt-upload" className="mb-1 block">
            Comprovante de pagamento
          </Label>
          <Input
            id="receipt-upload"
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-1">
            Envie uma foto ou screenshot do comprovante de pagamento
          </p>
        </div>
      </div>
    </div>
  );
};

export default PixPayment;
